import { Fragment, useContext, useState } from 'react';
import { GroupRequestModel } from 'ts/shared/classes/GroupRequestModel';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { dateFormat } from 'utils/ui';
import useRecaptcha from 'hooks/useRecaptcha';
import { sendGroupRequest } from 'services/myOrders';
import { showError, showSuccess } from 'utils/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { MainContext } from 'contexts/MainContext';

const GroupRequestPage = () => {
  const { user } = useContext(MainContext);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [model, setModel] = useState(new GroupRequestModel(user));
  const { handleRecapcha } = useRecaptcha();

  const requestTypes = [
    { value: 'Hotel', label: t('group.hotel') },
    { value: 'Flight', label: t('group.flight') },
  ];

  const roomTypes = [
    { value: 'Single', label: t('group.single') },
    { value: 'Double', label: t('group.double') },
    { value: 'Triple', label: t('group.triple') },
    { value: 'Quadro', label: t('group.quadro') },
  ];

  const stars = [
    { value: 1, label: t('group.star_1') },
    { value: 2, label: t('group.stars_2') },
    { value: 3, label: t('group.stars_3') },
    { value: 4, label: t('group.stars_4') },
    { value: 5, label: t('group.stars_5') },
  ];

  const onClickSubmit = async () => {
    setIsLoading(true);

    const token = await handleRecapcha();
    if (!token) {
      setIsLoading(false);
      return;
    }

    const res = await sendGroupRequest({ ...model, recaptchaToken: token });
    if (res && !res.hasErrors) {
      showSuccess(t('group.request_sent'));
    } else {
      showError(t('something_went_wrong'));
      console.log(res);
    }
    setIsLoading(false);
  };

  return (
    <div className="p-wrapper">
      <div className="title mb-20">
        <FontAwesomeIcon icon={faUsers} className="mr-5" style={{ fontSize: 18 }} />
        {t('group.gr')}/{t('group.request')}
      </div>
      <div className="book-form default-shadow">
        <div className="row">
          <div className="col-md-4 col-12">
            <div className="text-md text-bold mt-20 mb-30">{t('group.request')}</div>
            <div className="form-group">
              <div className="mb-5">{t('group.request_type')}</div>
              <SelectCustom
                options={requestTypes}
                value={model.requestType}
                onChange={(requestType) => setModel({ ...model, requestType })}
              />
            </div>

            {model.requestType === 'Flight' && (
              <Fragment>
                <FormGroup
                  label={t('group.from')}
                  value={model.flightRequest.from}
                  onChange={(from) =>
                    setModel({ ...model, flightRequest: { ...model.flightRequest, from } })
                  }
                />
                <FormGroup
                  label={t('group.to')}
                  value={model.flightRequest.to}
                  onChange={(to) =>
                    setModel({ ...model, flightRequest: { ...model.flightRequest, to } })
                  }
                />
                <div className="row">
                  <div className="col-md-6 col-12">
                    <FormGroupDatepicker
                      label={t('group.date')}
                      value={
                        model.flightRequest.date ? new Date(model.flightRequest.date) : undefined
                      }
                      onChange={(date) =>
                        setModel({
                          ...model,
                          flightRequest: {
                            ...model.flightRequest,
                            date: dateFormat(date, 'yyyy-MM-dd'),
                          },
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6 col-12">
                    <FormGroup
                      label={t('group.flight_time')}
                      value={model.flightRequest.flightTime}
                      onChange={(flightTime) =>
                        setModel({
                          ...model,
                          flightRequest: { ...model.flightRequest, flightTime },
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6 col-12">
                    <FormGroup
                      label={t('group.airline')}
                      value={model.flightRequest.airline}
                      onChange={(airline) =>
                        setModel({
                          ...model,
                          flightRequest: { ...model.flightRequest, airline },
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6 col-12">
                    <FormGroup
                      label={t('group.number_of_passengers')}
                      allowOnlyNumbers
                      value={model.flightRequest.numberOfPassengers}
                      onChange={(numberOfPassengers) =>
                        setModel({
                          ...model,
                          flightRequest: { ...model.flightRequest, numberOfPassengers },
                        })
                      }
                    />
                  </div>
                </div>
              </Fragment>
            )}
            {model.requestType === 'Hotel' && (
              <Fragment>
                <FormGroup
                  label={t('group.destination')}
                  value={model.hotelRequest.destination}
                  onChange={(destination) =>
                    setModel({ ...model, hotelRequest: { ...model.hotelRequest, destination } })
                  }
                />

                <div className="row">
                  <div className="col-md-8 col-12">
                    <FormGroup
                      label={t('group.hotel_name')}
                      value={model.hotelRequest.hotelName}
                      onChange={(hotelName) =>
                        setModel({ ...model, hotelRequest: { ...model.hotelRequest, hotelName } })
                      }
                    />
                  </div>
                  <div className="col-md-4 col-12">
                    <div className="form-group">
                      <div className="mb-5">{t('group.room_type')}</div>
                      <SelectCustom
                        options={roomTypes}
                        value={model.hotelRequest.roomType}
                        onChange={(roomType) =>
                          setModel({ ...model, hotelRequest: { ...model.hotelRequest, roomType } })
                        }
                      />
                    </div>
                  </div>
                </div>

                <FormGroup
                  label={t('group.group_type')}
                  value={model.hotelRequest.groupType}
                  onChange={(groupType) =>
                    setModel({ ...model, hotelRequest: { ...model.hotelRequest, groupType } })
                  }
                />

                <div className="row">
                  <div className="col-md-6 col-12">
                    <div className="form-group">
                      <div className="mb-5">{t('group.hotel_stars')}</div>
                      <SelectCustom
                        options={stars}
                        value={model.hotelRequest.stars}
                        onChange={(stars) =>
                          setModel({ ...model, hotelRequest: { ...model.hotelRequest, stars } })
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <FormGroup
                      label={t('group.number_of_rooms')}
                      value={model.hotelRequest.numberOfRooms}
                      allowOnlyNumbers
                      onChange={(numberOfRooms) =>
                        setModel({
                          ...model,
                          hotelRequest: {
                            ...model.hotelRequest,
                            numberOfRooms,
                          },
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6 col-12">
                    <FormGroupDatepicker
                      label={t('group.check_in')}
                      value={
                        model.hotelRequest.checkIn
                          ? new Date(model.hotelRequest.checkIn)
                          : undefined
                      }
                      onChange={(checkIn) =>
                        setModel({
                          ...model,
                          hotelRequest: {
                            ...model.hotelRequest,
                            checkIn: dateFormat(checkIn, 'yyyy-MM-dd'),
                          },
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6 col-12">
                    <FormGroupDatepicker
                      label={t('group.check_out')}
                      value={
                        model.hotelRequest.checkOut
                          ? new Date(model.hotelRequest.checkOut)
                          : undefined
                      }
                      onChange={(checkOut) =>
                        setModel({
                          ...model,
                          hotelRequest: {
                            ...model.hotelRequest,
                            checkOut: dateFormat(checkOut, 'yyyy-MM-dd'),
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </Fragment>
            )}
          </div>
          <div className="col-md-8 col-12">
            <div className="text-md text-bold mt-20 mb-30">{t('group.contact_information')}</div>
            <FormGroup
              label={t('group.agency_name')}
              value={model.contact.agencyName}
              onChange={(agencyName) =>
                setModel({ ...model, contact: { ...model.contact, agencyName } })
              }
            />

            <div className="row">
              <div className="col-md-6 col-12">
                <FormGroup
                  label={t('group.agent_fn')}
                  value={model.contact.firstName}
                  onChange={(firstName) =>
                    setModel({ ...model, contact: { ...model.contact, firstName } })
                  }
                />
              </div>
              <div className="col-md-6 col-12">
                <FormGroup
                  label={t('group.agent_email')}
                  value={model.contact.email}
                  onChange={(email) => setModel({ ...model, contact: { ...model.contact, email } })}
                />
              </div>
              <div className="col-md-6 col-12">
                <FormGroup
                  label={t('group.agent_ln')}
                  value={model.contact.lastName}
                  onChange={(lastName) =>
                    setModel({ ...model, contact: { ...model.contact, lastName } })
                  }
                />
              </div>
              <div className="col-md-6 col-12">
                <FormGroup
                  label={t('group.agent_phone')}
                  allowOnlyNumbers
                  value={model.contact.phone}
                  onChange={(phone) => setModel({ ...model, contact: { ...model.contact, phone } })}
                />
              </div>
              <div className="col-12">
                <div className="mb-5">{t('group.message')}</div>
                <textarea
                  className="form-control"
                  style={{ height: '180px', resize: 'none', outline: 'none' }}
                  value={model.contact.message}
                  onChange={(e) =>
                    setModel({ ...model, contact: { ...model.contact, message: e.target.value } })
                  }
                />
              </div>
              <div className="col-12  mt-20">
                <div className="flex-between">
                  <span></span>
                  <Button
                    title={t('group.send_request')}
                    onClick={onClickSubmit}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupRequestPage;
